import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    IconButton,
    Paper,
    useTheme,
} from "@mui/material";
import {
    Close as CloseIcon,
    IosShare as ShareIcon,
    AddBoxOutlined as AddIcon,
} from "@mui/icons-material";

const IOSInstallPrompt: React.FC = () => {
    const [showPrompt, setShowPrompt] = useState(false);
    const theme = useTheme();

    useEffect(() => {
        // Detect if the user is on iOS
        const isIos = /iphone|ipad|ipod/.test(
            window.navigator.userAgent.toLowerCase()
        );

        // Detect if the app is already in standalone mode
        const isStandalone =
            window.matchMedia("(display-mode: standalone)").matches ||
            (window.navigator as any).standalone;

        // Only show if on iOS and NOT in standalone mode
        if (isIos && !isStandalone) {
            // Check if user has already dismissed it in this session
            const isDismissed = sessionStorage.getItem("ios-prompt-dismissed");
            if (!isDismissed) {
                // Delay to not overwhelm user immediately
                const timer = setTimeout(() => {
                    setShowPrompt(true);
                }, 3000);
                return () => clearTimeout(timer);
            }
        }
    }, []);

    const handleClose = () => {
        setShowPrompt(false);
        sessionStorage.setItem("ios-prompt-dismissed", "true");
    };

    if (!showPrompt) return null;

    return (
        <Paper
            elevation={10}
            sx={{
                position: "fixed",
                bottom: 24,
                left: 16,
                right: 16,
                zIndex: 9999,
                padding: 2,
                borderRadius: 4,
                backgroundColor: theme.palette.background.paper,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                border: `1px solid ${theme.palette.divider}`,
                backgroundImage: "linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))",
                boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.4)",
                maxWidth: 500,
                margin: "0 auto",
                animation: "slideUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
                "@keyframes slideUp": {
                    from: { transform: "translateY(120%)", opacity: 0 },
                    to: { transform: "translateY(0)", opacity: 1 },
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.1rem" }}>
                    Install My Movies List
                </Typography>
                <IconButton size="small" onClick={handleClose}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                Install this app on your device for a better experience, offline access, and faster loading.
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    backgroundColor: theme.palette.action.hover,
                    p: 2,
                    borderRadius: 3,
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 0.5,
                        fontWeight: 500,
                    }}
                >
                    Tap the <ShareIcon fontSize="small" sx={{ color: theme.palette.primary.main, mb: 0.5 }} /> share button and then
                    <Box
                        component="span"
                        sx={{
                            backgroundColor: theme.palette.primary.main,
                            color: "white",
                            px: 1,
                            py: 0.2,
                            borderRadius: 1,
                            mx: 0.5,
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5
                        }}
                    >
                        Add to Home Screen <AddIcon fontSize="inherit" />
                    </Box>
                </Typography>
            </Box>
        </Paper>
    );
};

export default IOSInstallPrompt;
